export interface PaneWidthController {
  hiddenClass: string;
  defaultWidth: number;
  apply: () => void;
  set: (nextWidth: number) => void;
  persist: () => void;
  getWidth: () => number;
}

export function readStoredPaneWidth(
  storageKey: string,
  defaultWidth: number,
  minWidth: number,
  maxWidth: number,
): number {
  const stored = Number(localStorage.getItem(storageKey));
  if (!Number.isFinite(stored)) return defaultWidth;
  return Math.min(maxWidth, Math.max(minWidth, Math.round(stored)));
}

export function createPaneWidthController(options: {
  storageKey: string;
  cssVar: string;
  hiddenClass: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  workspaceEl: HTMLElement | null;
}): PaneWidthController {
  const { storageKey, cssVar, hiddenClass, defaultWidth, minWidth, maxWidth, workspaceEl } = options;
  let width = readStoredPaneWidth(storageKey, defaultWidth, minWidth, maxWidth);

  function clamp(value: number) {
    return Math.min(maxWidth, Math.max(minWidth, Math.round(value)));
  }

  function apply() {
    if (!workspaceEl) return;
    if (workspaceEl.classList.contains(hiddenClass)) {
      workspaceEl.style.removeProperty(cssVar);
      return;
    }
    workspaceEl.style.setProperty(cssVar, `${width}px`);
  }

  function set(nextWidth: number) {
    width = clamp(nextWidth);
    apply();
  }

  function persist() {
    localStorage.setItem(storageKey, String(width));
  }

  return { hiddenClass, defaultWidth, apply, set, persist, getWidth: () => width };
}

export function initPaneResize(
  selector: string,
  controller: PaneWidthController,
  workspaceEl: HTMLElement | null,
): void {
  const handle = document.querySelector(selector);
  if (!handle || !workspaceEl) return;

  handle.addEventListener("pointerdown", (event) => {
    if (workspaceEl.classList.contains(controller.hiddenClass)) return;
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    handle.classList.add("is-dragging");
    document.body.classList.add("is-resizing-pane");

    const startX = event.clientX;
    const startWidth = controller.getWidth();

    const onMove = (moveEvent: PointerEvent) => {
      controller.set(startWidth + (moveEvent.clientX - startX));
    };

    const finish = () => {
      handle.releasePointerCapture(event.pointerId);
      handle.classList.remove("is-dragging");
      document.body.classList.remove("is-resizing-pane");
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", finish);
      handle.removeEventListener("pointercancel", finish);
      controller.persist();
    };

    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", finish);
    handle.addEventListener("pointercancel", finish);
  });

  handle.addEventListener("dblclick", () => {
    if (workspaceEl.classList.contains(controller.hiddenClass)) return;
    controller.set(controller.defaultWidth);
    controller.persist();
  });
}
